import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Trash2, Plus, CheckCircle, Clock, CalendarDays } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { API } from '../config/api';
import { COMPANY_INFO } from '../data/siteData';
import { useServices } from '../context/ServicesContext';
import { Toaster, toast } from 'sonner';

export default function EditBooking() {
  const { id } = useParams();
  const { services } = useServices();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [notes, setNotes] = useState('');
  const [showAddService, setShowAddService] = useState(false);

  useEffect(() => {
    fetchBooking();
  }, [id]);

  const fetchBooking = async () => {
    try {
      const res = await axios.get(`${API}/bookings/${id}`);
      setBooking(res.data);
      setItems(res.data.items || []);
      setSelectedDate(res.data.date || '');
      setSelectedSlot(res.data.slot || '');
      setNotes(res.data.notes || '');
    } catch {
      setBooking(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedDate) return;
    axios.get(`${API}/bookings/availability`, { params: { date: selectedDate } })
      .then((res) => {
        let slots = res.data.available_slots;
        // Include current booking's slot as available
        if (booking && selectedDate === booking.date) {
          const currentSlot = booking.slot;
          if (!slots.find((s) => s.slot === currentSlot)) {
            const label = currentSlot === 'AM' ? 'Morning (8:00 AM – 11:30 AM)' : 'Afternoon (12:30 PM – 6:00 PM)';
            slots = [{ slot: currentSlot, label }, ...slots];
          }
        }
        setAvailableSlots(slots);
      })
      .catch(() => setAvailableSlots([]));
  }, [selectedDate, booking]);

  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const addServiceOption = (service, option) => {
    const price = parseFloat(option.price.replace(/[^0-9.]/g, ''));
    const exists = items.find((i) => i.service === service.name && i.option === option.label);
    if (exists) { toast.error('Already in your booking'); return; }
    setItems([...items, { service: service.name, option: option.label, price }]);
    toast.success(`${service.name} added`);
  };

  const handleSave = async () => {
    if (items.length === 0) { toast.error('Add at least one service'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/bookings/${id}`, {
        items: items.map((i) => ({ service: i.service, option: i.option, price: i.price })),
        date: selectedDate || undefined,
        slot: selectedSlot || undefined,
        notes: notes || undefined,
      });
      setSaved(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><p className="text-slate-400">Loading booking...</p></div>;

  if (!booking) return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="booking-not-found">
      <div className="text-center">
        <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3">Booking Not Found</h2>
        <Link to="/" className="text-sky-700 font-medium hover:underline">Back to Home</Link>
      </div>
    </div>
  );

  if (saved) return (
    <div className="min-h-[60vh] flex items-center justify-center py-16" data-testid="edit-booking-success">
      <div className="text-center max-w-md px-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">Booking Updated!</h2>
        <p className="text-slate-500 text-sm mb-6">Your changes have been saved. If you need further assistance, call us at <strong>{COMPANY_INFO.phone}</strong>.</p>
        <Link to="/" className="bg-sky-700 text-white font-semibold rounded-full px-8 py-3 hover:bg-sky-600 transition-colors inline-block">Back to Home</Link>
      </div>
    </div>
  );

  const total = items.reduce((s, i) => s + i.price, 0);
  const slotLabel = (s) => s === 'AM' ? 'Morning (8:00 AM – 11:30 AM)' : 'Afternoon (12:30 PM – 6:00 PM)';

  return (
    <div data-testid="edit-booking-page" className="py-8 md:py-16">
      <Toaster position="top-right" richColors />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2 text-slate-400 text-sm mb-6 hover:text-sky-700 transition-colors w-fit">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <div className="bg-white rounded-2xl border border-sky-100 shadow-lg overflow-hidden">
          <div className="bg-sky-700 text-white p-6">
            <h1 className="font-heading text-xl sm:text-2xl font-bold">Edit Booking {booking.booking_number}</h1>
            <p className="text-sky-200 text-sm mt-1">Modify your services, date, or time slot</p>
          </div>

          <div className="p-6 space-y-6">
            {/* Current services */}
            <div>
              <h3 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2">Your Services</h3>
              <div className="space-y-2" data-testid="edit-booking-items">
                {items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between bg-sky-50 rounded-xl px-4 py-3">
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{item.service}</p>
                      <p className="text-xs text-slate-400">{item.option}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-heading font-bold text-sky-700">${item.price.toFixed(2)}</span>
                      <button onClick={() => removeItem(i)} className="p-1 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-3">
                <button onClick={() => setShowAddService(!showAddService)} className="text-sky-700 text-sm font-medium hover:underline flex items-center gap-1">
                  <Plus className="w-4 h-4" /> Add more services
                </button>
                <p className="font-heading font-bold text-slate-900">Total: <span className="text-sky-700">${total.toFixed(2)}</span></p>
              </div>
            </div>

            {/* Add service panel */}
            {showAddService && (
              <div className="bg-sky-50 rounded-xl p-4 space-y-3" data-testid="add-service-panel">
                <p className="text-sm font-medium text-slate-700">Select a service to add:</p>
                {services.map((svc) => (
                  <div key={svc.slug} className="bg-white rounded-lg p-3">
                    <p className="font-semibold text-slate-900 text-sm mb-2">{svc.name}</p>
                    <div className="space-y-1">
                      {svc.pricing.map((opt) => {
                        const inBooking = items.some((i) => i.service === svc.name && i.option === opt.label);
                        return (
                          <div key={opt.label} className="flex items-center justify-between text-sm px-2 py-1.5 rounded hover:bg-sky-50">
                            <span className="text-slate-600">{opt.label}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sky-700 font-medium">{opt.price}</span>
                              <button
                                onClick={() => addServiceOption(svc, opt)}
                                disabled={inBooking}
                                className={`p-1 rounded ${inBooking ? 'text-green-500' : 'text-sky-600 hover:bg-sky-100'}`}
                              >
                                {inBooking ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Date & Slot */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1 flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Date</label>
                <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} data-testid="edit-booking-date" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Time Slot</label>
                {availableSlots.length > 0 ? (
                  <div className="space-y-1">
                    {availableSlots.map((s) => (
                      <button
                        key={s.slot}
                        type="button"
                        onClick={() => setSelectedSlot(s.slot)}
                        className={`w-full text-left px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                          selectedSlot === s.slot ? 'border-sky-700 bg-sky-50 text-sky-700' : 'border-sky-100 text-slate-600 hover:border-sky-300'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm py-2">Select a date to see available slots</p>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Notes</label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Any special instructions..." data-testid="edit-booking-notes" />
            </div>

            <Button onClick={handleSave} disabled={saving} data-testid="save-booking-btn" className="w-full bg-sky-700 text-white font-semibold rounded-full py-3 hover:bg-sky-600">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>

            <p className="text-slate-400 text-xs text-center">
              Need to reschedule to a different week? Call us at <strong>{COMPANY_INFO.phone}</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
